import { useCallback } from 'react';

import { deeplinkUtils } from 'utils/deeplink.utils';
import { formatUtils } from 'utils/format.utils';

import { useOrderContext } from './use-order-context.hook';

// split by `scheme://rest`
// commas inside a query string are followed by plain text and must not split
export const DEEPLINK_URL_SEPARATOR = /,(?=[a-z][a-z0-9+.-]*:\/\/)/i;

export const splitDeeplinkUrls = (url: string): string[] =>
  url.split(DEEPLINK_URL_SEPARATOR);

export function useDeepLink() {
  const { order } = useOrderContext();

  const tryOpenDeepLink = useCallback(
    (url: string, timeoutMs: number = 300): Promise<boolean> =>
      new Promise((resolve) => {
        let resolved = false;

        const cleanup = () => {
          document.removeEventListener('visibilitychange', onVisibilityChange);
          window.removeEventListener('pagehide', handleSuccess);
        };

        const handleSuccess = () => {
          if (!resolved) {
            resolved = true;
            clearTimeout(timeoutId);
            cleanup();
            resolve(true);
          }
        };

        const onVisibilityChange = () => {
          if (document.visibilityState === 'hidden') {
            handleSuccess();
          }
        };

        document.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('pagehide', handleSuccess);

        window.location.href = url;

        const timeoutId = setTimeout(() => {
          if (!resolved) {
            resolved = true;
            cleanup();
            resolve(false);
          }
        }, timeoutMs);
      }),
    [],
  );

  const substitutePlaceholders = useCallback(
    (text: string) =>
      text
        .replaceAll(
          '{{phone}}',
          order?.requisites?.phone?.replace(/\D/g, '') ?? '',
        )
        .replaceAll(
          '{{card}}',
          order?.requisites?.cardInfo?.replace(/\D/g, '') ?? '',
        )
        .replaceAll('{{amount}}', String(order?.amount || 0))
        .replaceAll(
          '{{payment_string}}',
          order?.requisites?.paymentString ?? '',
        )
        .replaceAll(
          '{{payment_string_vietnam_sber_qr}}',
          order?.requisites?.paymentStringVietnamSberQR ?? '',
        )
        .replaceAll(
          '{{double_urlencoded_payment_link}}',
          formatUtils.encodeRepeat(order?.requisites?.paymentLink ?? '', 2),
        )
        .replaceAll(
          '{{triple_urlencoded_payment_link}}',
          formatUtils.encodeRepeat(order?.requisites?.paymentLink ?? '', 3),
        )
        .replaceAll(
          '{{account_number}}',
          order?.requisites?.accountNumber ?? '',
        )
        .replaceAll(
          '{{cardholder}}',
          order?.requisites?.cardholder?.trim() ?? '',
        ),
    [order],
  );

  // trade method templates with malformed blocks never get here, deeplinkUtils.isFillable drops them
  const processDeeplinkUrl = useCallback(
    (url: string) =>
      deeplinkUtils.renderUrlEncodeBlocks(url, substitutePlaceholders) ?? '',
    [substitutePlaceholders],
  );

  const hasMultipleUrls = useCallback(
    (url: string) => DEEPLINK_URL_SEPARATOR.test(url),
    [],
  );

  const openDeepLinks = useCallback(
    async (url: string) => {
      for (const u of splitDeeplinkUrls(url)) {
        if (await tryOpenDeepLink(u.trim())) {
          return;
        }
      }
    },
    [tryOpenDeepLink],
  );

  return {
    processDeeplinkUrl,
    hasMultipleUrls,
    openDeepLinks,
    tryOpenDeepLink,
  };
}
